import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useSelector } from 'react-redux';

import { ServiceConstants } from 'constant';
import { PasswordRules } from 'types';
import { AppUtils, PasswordUtils } from 'utils';

import { IRootState } from 'config/redux';
import Modal from './base';
import Button from '../button/standard';
import PasswordInput from '../input/password';
import Text from '../text';
import { UserService } from 'services';
import { TFunction } from 'i18next';

const { REQUEST_PENDING, REQUEST_USER_CHANGE_PASSWORD } = ServiceConstants;

const RULES_MODEL: PasswordRules = {
  length: false,
  character: false,
};

interface ChangePasswordModalProps {
  opened: boolean;
  onClose: (val: boolean) => void;
  t: TFunction;
}

const ChangePasswordModal = ({ opened, onClose, t }: ChangePasswordModalProps) => {

  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USER_CHANGE_PASSWORD])

  const [rules, setRules] = useState<PasswordRules>(RULES_MODEL);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!opened) form.reset();
  }, [opened]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USER_CHANGE_PASSWORD,
      () => onClose(false),
      (err) => setError(err.data.message)
    );
  }, [serviceObject])

  const form = useForm({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    validate: {
      oldPassword: (value: string) => AppUtils.isEmpty(value) ? t('error.required') : null,
      newPassword: (value: string) => validatePassword(value),
      confirmPassword: (value: string) => validateConfirmPassword(value),
    },
    onValuesChange: (values, previous) => {
      const password = values.newPassword;
      setRules({
        character: PasswordUtils.checkIfHasSpecial(password),
        length: PasswordUtils.checkIfHas8Letters(password)
      });
    },
  });

  const validatePassword = useCallback((val: string) => {
    if (AppUtils.isEmpty(val)) {
      return t('error.required')
    }
    const keys = Object.keys(rules);
    if (keys.length !== keys.filter(el => rules[el as keyof PasswordRules]).length) {
      return '';
    }
    return null;
  }, [rules]);

  const validateConfirmPassword = useCallback((val: string) => {
    if (form.getTransformedValues().newPassword !== val) {
      return t('password.error.match')
    }
    return null;
  }, []);

  const isLoading = useMemo(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  const submit = useCallback(() => {
    setError(undefined);
    const { oldPassword, newPassword, confirmPassword } = form.getTransformedValues();
    UserService.changePassword(oldPassword, newPassword, confirmPassword);
  }, [form]);

  return (
    <Modal opened={opened} onClose={() => onClose(false)} title={t('password.change.title')}>
      <Box>
        <PasswordInput
          label={t('password.old.title')}
          key={form.key('oldPassword')}
          {...form.getInputProps('oldPassword')}
        />
        <Box mt={'sm'}>
          <PasswordInput
            label={t('password.title')}
            key={form.key('newPassword')}
            {...form.getInputProps('newPassword')}
          />
          <Box mt={'sm'}>
            {Object.keys(rules).map((el: any) => (
              <Group key={`rule-${el}`}>
                {rules[el as keyof PasswordRules]
                  ? <IconCheck size={20} color={'var(--mantine-primary-color-9)'} />
                  : <IconX size={20} color={'var(--mantine-color-error)'} />}
                <Text mb={0} c={rules[el as keyof PasswordRules] ? 'var(--mantine-primary-color-9)' : 'var(--mantine-color-error)'}>{t('password.rules.' + el)}</Text>
              </Group>
            ))}
          </Box>
        </Box>
      </Box>
      <Box mt={'sm'}>
        <PasswordInput
          label={t('password.confirm.title')}
          key={form.key('confirmPassword')}
          {...form.getInputProps('confirmPassword')}
        />
        {error && <Text c={'var(--mantine-color-error)'} mt={'md'}>{error}</Text>}
      </Box>
      <Box mt={40}>
        <Button
          fullWidth={true}
          disabled={!form.isValid() || isLoading}
          loading={isLoading}
          onClick={submit}
        >
          {t('action.submit')}
        </Button>
      </Box>
    </Modal>
  )
}

export default ChangePasswordModal;